import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";

export default function AuthPage({ mode = "login" }) {
  const [tab, setTab] = useState(mode); // "login" or "register"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);
  const { login, register, user, isAdmin } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      if (isAdmin) navigate("/admin");
      else navigate("/dashboard");
    }
  }, [user, isAdmin, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast.error("Fill in all fields.");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      // redirect handled by useEffect
    } catch {
      toast.error("Invalid email or password.");
    } finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) return toast.error("Fill in all fields.");
    if (password.length < 6) return toast.error("Password must be at least 6 characters.");
    setLoading(true);
    try {
      await register(email, password, name);
      setRegistered(true);
    } catch (err) {
      if (err.code === "auth/email-already-in-use") toast.error("Email already registered. Please login.");
      else toast.error("Registration failed. Try again.");
    } finally { setLoading(false); }
  };

  if (registered) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#050505", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: "20%", left: "20%", width: 600, height: 600, borderRadius: "50%", background: "radial-gradient(ellipse,#4ade8008 0%,transparent 70%)", animation: "glowOrb 8s ease-in-out infinite", pointerEvents: "none" }} />
        <div style={{ position: "relative", zIndex: 1, textAlign: "center", maxWidth: 480, padding: "0 24px" }}>
          <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#4ade8015", border: "1px solid #4ade8040", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 28px", boxShadow: "0 0 32px #4ade8030" }}>
            <span style={{ fontSize: "1.8rem", color: "#4ade80" }}>✓</span>
          </div>
          <h2 style={{ fontFamily: "'Barlow',sans-serif", fontWeight: 700, fontSize: "1.6rem", textTransform: "uppercase", color: "#f0f0f0", marginBottom: 16 }}>Registration Submitted</h2>
          <p style={{ fontSize: "15px", color: "#555", lineHeight: 1.85, marginBottom: 32 }}>
            Your account is pending approval. Once the admin approves your account, you will be able to log in and access the members area.
          </p>
          <Link to="/" style={{ fontFamily: "var(--font-mono)", fontSize: "11px", letterSpacing: "0.14em", textTransform: "uppercase", color: "#c9a84c", textDecoration: "none" }}>← Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#050505", position: "relative", overflow: "hidden" }}>
      <style>{`
        @keyframes glowOrb { 0%,100%{opacity:.35} 50%{opacity:.8} }
        @keyframes lineIn { from{transform:scaleX(0)} to{transform:scaleX(1)} }
        @keyframes fadeUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
        @keyframes rotateRing { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes dotBlink { 0%,100%{opacity:1} 50%{opacity:0.3} }
      `}</style>

      {/* Background */}
      <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(#ffffff04 1px,transparent 1px),linear-gradient(90deg,#ffffff04 1px,transparent 1px)", backgroundSize: "64px 64px", pointerEvents: "none" }} />
      <div style={{ position: "absolute", top: "10%", left: "15%", width: 700, height: 700, borderRadius: "50%", background: "radial-gradient(ellipse,#c9a84c07 0%,transparent 65%)", animation: "glowOrb 9s ease-in-out infinite", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: "5%", right: "10%", width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(ellipse,#818cf808 0%,transparent 70%)", animation: "glowOrb 7s ease-in-out infinite reverse", pointerEvents: "none" }} />

      {/* Rotating rings */}
      <div style={{ position: "absolute", top: "50%", left: "50%", width: 700, height: 700, border: "1px solid #c9a84c06", borderRadius: "50%", transform: "translate(-50%,-50%)", animation: "rotateRing 25s linear infinite", pointerEvents: "none" }} />
      <div style={{ position: "absolute", top: "50%", left: "50%", width: 500, height: 500, border: "1px solid #c9a84c08", borderRadius: "50%", transform: "translate(-50%,-50%)", animation: "rotateRing 18s linear infinite reverse", pointerEvents: "none" }} />

      <div style={{ position: "relative", zIndex: 1, width: "100%", maxWidth: 440, padding: "0 24px", animation: "fadeUp 0.6s ease both" }}>

        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <Link to="/" style={{ textDecoration: "none" }}>
            <div style={{ fontFamily: "'Barlow',sans-serif", fontWeight: 700, fontSize: "1.4rem", textTransform: "uppercase", letterSpacing: "0.1em", color: "#f0f0f0", marginBottom: 4 }}>Macro Trader</div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.22em", color: "#c9a84c", textTransform: "uppercase" }}>Research & Analysis</div>
          </Link>
        </div>

        {/* Card */}
        <div style={{ background: "#0a0a0a", border: "1px solid #1e1e1e", borderRadius: "12px", overflow: "hidden", boxShadow: "0 32px 80px rgba(0,0,0,0.6), 0 0 40px #c9a84c08" }}>
          {/* Top glow line */}
          <div style={{ height: 2, background: "linear-gradient(90deg,transparent,#c9a84c,#f5c518,#c9a84c,transparent)" }} />

          {/* Tabs */}
          <div style={{ display: "flex", borderBottom: "1px solid #141414" }}>
            {["login", "register"].map(t => (
              <button key={t} onClick={() => setTab(t)} style={{
                flex: 1, padding: "16px", background: "transparent", border: "none", cursor: "pointer",
                fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.16em", textTransform: "uppercase",
                color: tab === t ? "#f5c518" : "#333",
                borderBottom: tab === t ? "2px solid #f5c518" : "2px solid transparent",
                transition: "all 0.2s",
                boxShadow: tab === t ? "0 4px 20px #f5c51815" : "none",
              }}>
                {t === "login" ? "Sign In" : "Register"}
              </button>
            ))}
          </div>

          <div style={{ padding: "36px 36px 32px" }}>
            {tab === "login" ? (
              <form onSubmit={handleLogin}>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.16em", textTransform: "uppercase", color: "#444", display: "block", marginBottom: 8 }}>Email</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required className="form-input" style={{ width: "100%", boxSizing: "border-box" }} />
                </div>
                <div style={{ marginBottom: 28 }}>
                  <label style={{ fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.16em", textTransform: "uppercase", color: "#444", display: "block", marginBottom: 8 }}>Password</label>
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required className="form-input" style={{ width: "100%", boxSizing: "border-box" }} />
                </div>
                <button type="submit" disabled={loading} style={{ width: "100%", padding: "13px", background: loading ? "#1a1a1a" : "#c9a84c18", border: "1px solid #c9a84c40", borderRadius: "8px", color: "#f5c518", fontFamily: "var(--font-mono)", fontSize: "11px", letterSpacing: "0.18em", textTransform: "uppercase", cursor: loading ? "not-allowed" : "pointer", transition: "all 0.2s", boxShadow: loading ? "none" : "0 0 20px #c9a84c15" }}
                  onMouseEnter={e => { if (!loading) { e.currentTarget.style.background = "#c9a84c28"; e.currentTarget.style.boxShadow = "0 0 32px #c9a84c25"; }}}
                  onMouseLeave={e => { e.currentTarget.style.background = "#c9a84c18"; e.currentTarget.style.boxShadow = "0 0 20px #c9a84c15"; }}
                >
                  {loading ? "Signing in..." : "Sign In →"}
                </button>
              </form>
            ) : (
              <form onSubmit={handleRegister}>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.16em", textTransform: "uppercase", color: "#444", display: "block", marginBottom: 8 }}>Full Name</label>
                  <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Your name" required className="form-input" style={{ width: "100%", boxSizing: "border-box" }} />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.16em", textTransform: "uppercase", color: "#444", display: "block", marginBottom: 8 }}>Email</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required className="form-input" style={{ width: "100%", boxSizing: "border-box" }} />
                </div>
                <div style={{ marginBottom: 28 }}>
                  <label style={{ fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.16em", textTransform: "uppercase", color: "#444", display: "block", marginBottom: 8 }}>Password</label>
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 6 characters" required className="form-input" style={{ width: "100%", boxSizing: "border-box" }} />
                </div>
                <button type="submit" disabled={loading} style={{ width: "100%", padding: "13px", background: loading ? "#1a1a1a" : "#4ade8012", border: "1px solid #4ade8035", borderRadius: "8px", color: "#4ade80", fontFamily: "var(--font-mono)", fontSize: "11px", letterSpacing: "0.18em", textTransform: "uppercase", cursor: loading ? "not-allowed" : "pointer", transition: "all 0.2s", boxShadow: loading ? "none" : "0 0 20px #4ade8010" }}
                  onMouseEnter={e => { if (!loading) { e.currentTarget.style.background = "#4ade8020"; e.currentTarget.style.boxShadow = "0 0 32px #4ade8020"; }}}
                  onMouseLeave={e => { e.currentTarget.style.background = "#4ade8012"; e.currentTarget.style.boxShadow = "0 0 20px #4ade8010"; }}
                >
                  {loading ? "Creating account..." : "Create Account →"}
                </button>
                <p style={{ marginTop: 16, fontSize: "11px", color: "#333", fontFamily: "var(--font-mono)", lineHeight: 1.7, textAlign: "center" }}>
                  Your account will be reviewed and approved by the admin before you can access the members area.
                </p>
              </form>
            )}
          </div>
        </div>

        <p style={{ textAlign: "center", marginTop: 24, fontSize: "10px", color: "#222", fontFamily: "var(--font-mono)", letterSpacing: "0.08em" }}>
          <Link to="/" style={{ color: "#333", textDecoration: "none" }}>← Back to Home</Link>
        </p>
      </div>
    </div>
  );
}
